import { useEffect, useRef, useState } from "react";
import { Sparkles, Check, X, Loader2 } from "lucide-react";

// ═══════════════════════════════════════════════════════════════════════
// BuildingScreen — full-screen overlay that streams /execute progress
// while the Agentic Operating System evolves. Three phases:
//   - building: SSE stream is live, steps render with status icons
//   - done:     all steps complete, summary visible, Show me button
//   - error:    a step failed, error visible, Close button
//
// Mounted by Operator.tsx and EvolutionCenter.tsx when the user clicks
// [Yes, evolve] on a proposal. The proposal has already been APPROVED
// by the time this mounts — this component only drives /execute.
// ═══════════════════════════════════════════════════════════════════════

const STEP_LABELS: Record<string, string> = {
  load_proposal:       "Reading proposal",
  load_os:             "Loading your Agentic Operating System",
  apply_schema_diff:   "Designing the change",
  persist_schema:      "Updating the Agentic Operating System",
  migrate_screen_data: "Migrating your data",
  mark_applied:        "Finalizing",
  stage_agent_deployment: "Adding specialist agent",
};

const STEP_ORDER = [
  "load_proposal",
  "load_os",
  "apply_schema_diff",
  "persist_schema",
  "stage_agent_deployment",
  "migrate_screen_data",
  "mark_applied",
];

type StepStatus = "pending" | "running" | "complete" | "error";
type Phase = "building" | "done" | "error";

interface BuildingScreenProps {
  proposalId: string;
  proposalTitle: string;
  onClose: () => void;     // user closed without action (pre-execute or on error)
  // EVOLUTION-3e — onApplied now receives what the proposal touched so the
  // caller can navigate to the affected screen and trigger Spotlight.
  onApplied: (info: { target_screen_id?: string; target_field_key?: string }) => void;
}

export function BuildingScreen({ proposalId, proposalTitle, onClose, onApplied }: BuildingScreenProps) {
  const [stepStatus, setStepStatus] = useState<Record<string, StepStatus>>({});
  const [phase, setPhase] = useState<Phase>("building");
  const [summary, setSummary] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [failedStep, setFailedStep] = useState<string>("");
  // EVOLUTION-3e — captured from the final complete SSE event so the
  // Show me button can navigate to the affected screen and spotlight
  // the field.
  const [targetScreen, setTargetScreen] = useState<string>("");
  const [targetField, setTargetField] = useState<string>("");
  // StrictMode dev guard — without this React 18 dev mode would fire two
  // parallel /execute POSTs because effects re-run after the initial mount.
  // Production isn't affected; the guard is cheap insurance.
  const startedRef = useRef(false);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    let cancelled = false;
    let reader: ReadableStreamDefaultReader<Uint8Array> | null = null;

    (async () => {
      try {
        const res = await fetch(`/api/evolution/proposals/${proposalId}/execute`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
        });

        if (!res.ok || !res.body) {
          setPhase("error");
          setErrorMsg(`Request failed: ${res.status}`);
          return;
        }

        reader = res.body.getReader();
        const decoder = new TextDecoder();
        let buffer = "";

        while (!cancelled) {
          const { done, value } = await reader.read();
          if (done) break;
          buffer += decoder.decode(value, { stream: true });

          // SSE frames are separated by \n\n. Each frame in our protocol
          // carries a single "data: {json}" line. Trailing partial frame
          // stays in `buffer` until the next read completes it.
          const frames = buffer.split("\n\n");
          buffer = frames.pop() || "";

          for (const frame of frames) {
            const line = frame.trim();
            if (!line.startsWith("data:")) continue;
            const payload = line.slice(5).trim();
            if (!payload) continue;

            let event: any;
            try { event = JSON.parse(payload); } catch { continue; }

            if (event.kind === "step_start" && typeof event.step === "string") {
              setStepStatus(prev => ({ ...prev, [event.step]: "running" }));
            } else if (event.kind === "step_complete" && typeof event.step === "string") {
              setStepStatus(prev => ({ ...prev, [event.step]: "complete" }));
            } else if (event.kind === "step_error" && typeof event.step === "string") {
              setStepStatus(prev => ({ ...prev, [event.step]: "error" }));
              setFailedStep(event.step);
              setErrorMsg(event.error || "Step failed");
            } else if (event.kind === "summary" && typeof event.summary === "string") {
              setSummary(event.summary);
            } else if (event.kind === "complete") {
              if (typeof event.summary === "string") setSummary(event.summary);
              if (typeof event.target_screen_id === "string") setTargetScreen(event.target_screen_id);
              if (typeof event.target_field_key === "string") setTargetField(event.target_field_key);
              // EVOLUTION-3e — auto-mark migrate_screen_data complete if
              // the step never ran. Happens for new_field / new_screen —
              // zero post_actions means the executor doesn't emit any
              // events for that step, leaving it grey forever.
              setStepStatus(prev =>
                prev.migrate_screen_data && prev.migrate_screen_data !== "pending"
                  ? prev
                  : { ...prev, migrate_screen_data: "complete" }
              );
              setPhase("done");
            } else if (event.kind === "fatal") {
              setPhase("error");
              setErrorMsg(event.error || "Execution failed");
              if (event.failed_step) setFailedStep(event.failed_step);
            }
          }
        }
      } catch (err: any) {
        if (!cancelled) {
          setPhase("error");
          setErrorMsg(err?.message || "Network error");
        }
      }
    })();

    return () => {
      cancelled = true;
      if (reader) {
        try { reader.cancel(); } catch { /* noop */ }
      }
    };
  }, [proposalId]);

  return (
    <div className="fixed inset-0 z-[400] flex items-center justify-center bg-slate-900/85 backdrop-blur-md p-4">
      <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-xl overflow-hidden">

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100 bg-gradient-to-r from-amber-50 to-amber-100/50">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-amber-200 flex items-center justify-center">
              {phase === "done" ? (
                <Check size={20} className="text-amber-800" />
              ) : phase === "error" ? (
                <X size={20} className="text-red-700" />
              ) : (
                <Sparkles size={20} className="text-amber-800" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-widest text-amber-800 mb-0.5">
                {phase === "done" ? "Evolution Complete"
                  : phase === "error" ? "Evolution Failed"
                  : "Evolving Your Agentic Operating System"}
              </div>
              <div className="text-base font-semibold text-slate-900 truncate">
                {proposalTitle}
              </div>
            </div>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 py-5">
          {/* Step list — visible in all phases so the user can see what happened */}
          <div className="space-y-2.5 mb-5">
            {STEP_ORDER.map(step => {
              const status = stepStatus[step] || "pending";
              const label = STEP_LABELS[step] || step;
              return (
                <div key={step} className="flex items-center gap-3">
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 ${
                    status === "complete" ? "bg-emerald-500" :
                    status === "running" ? "bg-amber-500" :
                    status === "error" ? "bg-red-500" :
                    "bg-slate-200"
                  }`}>
                    {status === "complete" && <Check size={12} className="text-white" strokeWidth={3} />}
                    {status === "running" && <Loader2 size={12} className="text-white animate-spin" />}
                    {status === "error" && <X size={12} className="text-white" strokeWidth={3} />}
                  </div>
                  <span className={`text-sm ${
                    status === "complete" ? "text-slate-900 font-medium" :
                    status === "running" ? "text-amber-900 font-medium" :
                    status === "error" ? "text-red-700 font-medium" :
                    "text-slate-400"
                  }`}>
                    {label}
                  </span>
                </div>
              );
            })}
          </div>

          {phase === "done" && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 mb-1">
                What changed
              </div>
              <p className="text-sm text-slate-900 leading-relaxed">{summary || "Your Agentic Operating System update is now live."}</p>
            </div>
          )}

          {phase === "error" && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-5">
              <div className="text-[10px] font-bold uppercase tracking-wider text-red-700 mb-1">
                {failedStep ? `Failed at: ${STEP_LABELS[failedStep] || failedStep}` : "Error"}
              </div>
              <p className="text-sm text-red-900 leading-relaxed">{errorMsg}</p>
              <p className="text-xs text-slate-600 mt-2">
                The proposal is still in the Evolution Center. You can try again later.
              </p>
            </div>
          )}

          <div className="flex justify-end gap-2">
            {phase === "building" && (
              <span className="text-xs text-slate-400 italic self-center">
                Please don't close this window
              </span>
            )}
            {phase === "done" && (
              <button
                onClick={() => onApplied({
                  target_screen_id: targetScreen || undefined,
                  target_field_key: targetField || undefined,
                })}
                className="px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold transition-colors"
              >
                Show me
              </button>
            )}
            {phase === "error" && (
              <button
                onClick={onClose}
                className="px-4 py-2 rounded-lg bg-white hover:bg-slate-50 text-slate-700 text-sm font-medium border border-slate-300 transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
